import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { RotateCcw, PenLine, Send, AlertTriangle, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, StatusChip, SecureImage, usePoll, displayName } from "@/components/common";

function missing(c) {
  const m = [];
  if (!c.emails?.length) m.push("email");
  if (!c.full_name) m.push("name");
  if (!c.company) m.push("company");
  return m;
}

export default function BatchReview() {
  const { id } = useParams();
  const nav = useNavigate();
  const [b, setB] = useState(null);
  const [sel, setSel] = useState(new Set());
  usePoll(() => api.get(`/batches/${id}`).then((r) => setB(r.data)).catch((e) => toast.error(errMsg(e))), 3000, [id]);
  if (!b) return <p className="text-slate-500">Loading…</p>;

  const act = async (path, msg) => { try { const r = await api.post(path); toast.success(msg); return r.data; } catch (e) { toast.error(errMsg(e)); } };
  const counts = b.captures.reduce((a, c) => ({ ...a, [c.status]: (a[c.status] || 0) + 1 }), {});
  const allContacts = b.captures.flatMap((c) => c.contacts);
  const toggle = (cid) => setSel((s) => { const n = new Set(s); n.has(cid) ? n.delete(cid) : n.add(cid); return n; });

  return (
    <div>
      <PageHeader title={b.name} subtitle={`${b.captures.length} image(s) · ${allContacts.length} contact(s)`}
        actions={<button data-testid="batch-email-selected-btn" disabled={!sel.size} onClick={() => nav(`/campaigns/new?ids=${[...sel].join(",")}`)} className="cd-btn-primary"><Send className="h-4 w-4" />Email {sel.size || ""} selected</button>} />
      <div className="mb-4 flex flex-wrap gap-2" data-testid="batch-counts">{Object.entries(counts).map(([s, n]) => <span key={s} className="flex items-center gap-1 text-sm">{n}<StatusChip status={s} /></span>)}</div>
      <div className="mb-2 flex items-center gap-2 text-sm">
        <input type="checkbox" data-testid="batch-select-all" className="h-5 w-5 accent-teal-600" onChange={(e) => setSel(e.target.checked ? new Set(allContacts.filter((c) => c.status !== "needs_review").map((c) => c.id)) : new Set())} />
        Select all reviewed contacts
      </div>
      <div className="space-y-2">
        {b.captures.map((cap) => (
          <div key={cap.id} className="cd-card flex flex-col gap-3 p-3 sm:flex-row" data-testid={`batch-capture-${cap.id}`}>
            <div className="flex gap-1 sm:w-40">{cap.file_ids.map((f) => <SecureImage key={f} fileId={f} className="h-20 w-full rounded-lg bg-slate-50 object-cover sm:w-20" />)}</div>
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex flex-wrap items-center gap-2"><StatusChip status={cap.status} testid={`batch-status-${cap.id}`} />
                {cap.cards_detected > 1 && <span className="text-xs font-semibold text-amber-700">{cap.cards_detected} cards in image</span>}
              </div>
              {cap.contacts.map((c) => (
                <div key={c.id} className="flex items-start gap-2 border-t border-slate-100 py-2 first:border-0">
                  <input type="checkbox" data-testid={`batch-select-${c.id}`} className="mt-1 h-5 w-5 accent-teal-600" checked={sel.has(c.id)} onChange={() => toggle(c.id)} />
                  <Link to={`/contacts/${c.id}?review=1`} className="grid min-w-0 flex-1 gap-0.5 sm:grid-cols-[2fr_2fr_1.5fr]">
                    <div className="min-w-0"><p className="truncate font-semibold">{displayName(c)}</p><p className="truncate text-xs text-slate-500">{c.company}</p></div>
                    <p className="flex items-center gap-1 truncate text-sm">{c.emails?.[0]?.uncertain && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}{c.emails?.[0]?.value || <span className="text-slate-400">No email</span>}</p>
                    <div className="text-xs text-slate-500">
                      <p>{c.contact_type} · {c.industry}</p>
                      {missing(c).length > 0 && <p className="text-amber-700">Missing: {missing(c).join(", ")}</p>}
                      {c.duplicate_candidates?.length > 0 && <p className="flex items-center gap-1 font-semibold text-amber-700"><Copy className="h-3 w-3" />Possible duplicate</p>}
                    </div>
                  </Link>
                  <StatusChip status={c.status} />
                </div>
              ))}
              {cap.status === "failed" && (
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm text-rose-700">{cap.error} — image kept.</p>
                  <button data-testid={`batch-retry-${cap.id}`} onClick={() => act(`/captures/${cap.id}/retry`, "Retrying")} className="cd-btn-secondary h-9 px-3"><RotateCcw className="h-4 w-4" />Retry</button>
                  <button data-testid={`batch-manual-${cap.id}`} onClick={async () => { const d = await act(`/captures/${cap.id}/manual`, "Manual entry"); d && nav(`/contacts/${d.contact_id}?review=1`); }} className="cd-btn-ghost h-9 px-3"><PenLine className="h-4 w-4" />Enter manually</button>
                </div>
              )}
              {cap.extra_cards?.length > 0 && (
                <button data-testid={`batch-split-${cap.id}`} onClick={() => act(`/captures/${cap.id}/split`, "Separate contacts created")} className="cd-btn-secondary mt-1 h-9 px-3">Create {cap.extra_cards.length} more contact(s) from this image</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
